import { Link, NavLink, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { AppHeader } from "@/components/AppHeader";
import { AiAssistantPanel } from "@/components/AiAssistantPanel";
import { cases } from "@/lib/endpoints";

interface Step {
  slug: string;
  label: string;
  hint?: string;
  timeEst: string; // e.g. "~2 min"
}

export const WIZARD_STEPS: Step[] = [
  { slug: "personal",   label: "Personal info",     hint: "Name, DOB, citizenship",   timeEst: "~3 min" },
  { slug: "biographic", label: "Biographic info",    hint: "Height, eye color, race",   timeEst: "~1 min" },
  { slug: "addresses",  label: "Addresses",          hint: "Home + 5-yr history",       timeEst: "~3 min" },
  { slug: "employment", label: "Employment",         hint: "Last 5 years",              timeEst: "~2 min" },
  { slug: "travel",     label: "Travel",             hint: "Trips outside the U.S.",    timeEst: "~4 min" },
  { slug: "marriage",   label: "Marital history",    hint: "All marriages",             timeEst: "~2 min" },
  { slug: "children",   label: "Children",           hint: "Biological & adopted",      timeEst: "~1 min" },
  { slug: "criminal",   label: "Background",         hint: "Arrests, convictions",      timeEst: "~3 min" },
  { slug: "documents",  label: "Documents",          hint: "Supporting docs upload",    timeEst: "~3 min" },
  { slug: "oath",       label: "Oath",               hint: "Allegiance declaration",    timeEst: "~1 min" },
  { slug: "contact",    label: "Contact & signature", hint: "Phone, email, sign date",  timeEst: "~1 min" },
  { slug: "fee-reduction", label: "Fee reduction",   hint: "Optional — skip if paying full", timeEst: "~1 min" },
  { slug: "interpreter", label: "Interpreter",       hint: "Optional — if someone translated", timeEst: "~1 min" },
  { slug: "preparer",   label: "Preparer",           hint: "Optional — if attorney filled form", timeEst: "~1 min" },
  { slug: "checkout",   label: "Filing fee",         hint: "Pay USCIS fee",             timeEst: "~1 min" },
  { slug: "review",     label: "Review & generate",  hint: "Download your packet",      timeEst: "~2 min" },
];

const TOTAL_MIN = WIZARD_STEPS.reduce((sum, s) => {
  return sum + parseInt(s.timeEst.replace(/\D/g, ""), 10);
}, 0);

export function WizardLayout() {
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  // Extract current step slug from the URL: /cases/:id/wizard/:slug
  const step = pathname.split("/wizard/")[1]?.split("/")[0] ?? undefined;

  // Eligibility gate. Until the chat-gating flow on /workspace finishes
  // and writes eligibility_status="eligible", the wizard isn't usable —
  // every form-mapping rule assumes the case has been screened in.
  // Bounce the user back to the workspace so they finish gating there.
  const caseQ = useQuery({
    queryKey: ["case", id],
    queryFn: () => cases.get(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });

  if (!id) return null;
  if (caseQ.isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (caseQ.data && caseQ.data.eligibility_status !== "eligible") {
    return <Navigate to={`/cases/${id}/workspace`} replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-100">
      <AppHeader />

      <div className="flex flex-1 overflow-hidden">
        {/* ── Sidebar ─────────────────────────────────────────────────── */}
        <aside className="w-64 shrink-0 bg-white border-r border-slate-200/70 hidden md:flex flex-col sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto">

          {/* Back link */}
          <div className="px-5 pt-5 pb-3 border-b border-slate-100">
            <Link
              to={`/cases/${id}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-blue-600 transition-colors"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Case overview
            </Link>
          </div>

          {/* Steps header */}
          <div className="px-5 pt-4 pb-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Application Steps
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              About {TOTAL_MIN} min total
            </p>
          </div>

          {/* Step list */}
          <nav className="flex-1 px-3 pb-4 space-y-0.5">
            {WIZARD_STEPS.map((step, idx) => (
              <NavLink
                key={step.slug}
                to={`/cases/${id}/wizard/${step.slug}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-start gap-2.5 rounded-xl px-3 py-2.5 text-sm transition-all duration-150 group",
                    isActive
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Step number */}
                    <span className={cn(
                      "mt-0.5 text-xs tabular-nums w-5 text-right shrink-0 font-semibold",
                      isActive ? "text-blue-200" : "text-slate-400",
                    )}>
                      {String(idx + 1).padStart(2, "0")}
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="font-semibold leading-snug">{step.label}</div>
                      {step.hint && (
                        <div className={cn(
                          "text-[11px] mt-0.5 truncate",
                          isActive ? "text-blue-200" : "text-slate-400",
                        )}>
                          {step.hint}
                        </div>
                      )}
                    </div>

                    {/* Time estimate badge */}
                    <span className={cn(
                      "text-[10px] font-medium shrink-0 mt-0.5 px-1.5 py-0.5 rounded-md",
                      isActive
                        ? "bg-blue-500/40 text-blue-100"
                        : "bg-slate-100 text-slate-400 group-hover:bg-slate-200",
                    )}>
                      {step.timeEst}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Trust footer */}
          <div className="px-5 py-4 border-t border-slate-100 space-y-2.5">
            {[
              { icon: "🔒", text: "Bank-grade encryption" },
              { icon: "📋", text: "USCIS Form N-400 · 2025" },
              { icon: "💾", text: "Progress saved automatically" },
            ].map(({ icon, text }) => (
              <div key={text} className="flex items-center gap-2 text-[11px] text-slate-400">
                <span className="text-sm">{icon}</span>
                {text}
              </div>
            ))}
          </div>
        </aside>

        {/* ── Content ─────────────────────────────────────────────────── */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-6 py-10">
            <Outlet />
          </div>
        </main>
      </div>

      {/* AI assistant — floating button + slide-in panel */}
      <AiAssistantPanel caseId={id} wizardStep={step} />
    </div>
  );
}
